import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { ViewMode } from "@/components/common/ViewToggle";
import { useSettings } from "@/providers/SettingsProvider";

export type TasksSortField = "name" | "dateCreated" | "dueDate" | "priority";
export type TasksSortOrder = "asc" | "desc";

interface TasksFilterContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  statusFilter: string[];
  setStatusFilter: (status: string[]) => void;
  priorityFilter: string[];
  setPriorityFilter: (priority: string[]) => void;
  maxDaysAgo: number | null;
  setMaxDaysAgo: (days: number | null) => void;
  showCancelled: boolean;
  setShowCancelled: (show: boolean) => void;
  ownershipFilter: 'my-tasks' | 'assigned-to-me' | 'all';
  setOwnershipFilter: (filter: 'my-tasks' | 'assigned-to-me' | 'all') => void;
  sortField: TasksSortField;
  setSortField: (field: TasksSortField) => void;
  sortOrder: TasksSortOrder;
  setSortOrder: (order: TasksSortOrder) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

const TasksFilterContext = createContext<TasksFilterContextType | undefined>(undefined);

interface TasksFilterProviderProps {
  children: ReactNode;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

export const TasksFilterProvider = ({ children, viewMode, setViewMode }: TasksFilterProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getDaysFilterForEntity, setDaysFilterForEntity } = useSettings();

  // Initialize state from URL parameters
  const [searchQuery, setSearchQueryState] = useState(() => searchParams.get("search") || "");
  const [statusFilter, setStatusFilterState] = useState<string[]>(() => {
    const status = searchParams.get("status");
    // Default to all statuses except cancelled
    return status ? status.split(",") : ["todo", "inProgress", "done"];
  });
  const [priorityFilter, setPriorityFilterState] = useState<string[]>(() => {
    const priority = searchParams.get("priority");
    return priority ? priority.split(",") : [];
  });
  const [maxDaysAgo, setMaxDaysAgoState] = useState<number | null>(() => {
    const days = searchParams.get("days");
    return days ? parseInt(days, 10) : getDaysFilterForEntity('tasks');
  });
  const [showCancelled, setShowCancelledState] = useState(() => {
    const show = searchParams.get("showCancelled");
    return show === "true";
  });
  const [ownershipFilter, setOwnershipFilterState] = useState<'my-tasks' | 'assigned-to-me' | 'all'>(() => {
    const ownership = searchParams.get("ownership");
    return (ownership as 'my-tasks' | 'assigned-to-me' | 'all') || 'all';
  });
  const [sortField, setSortFieldState] = useState<TasksSortField>("dateCreated");
  const [sortOrder, setSortOrderState] = useState<TasksSortOrder>("desc");
  
  // Sync URL parameters whenever filters change
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    
    // Preserve the task parameter if it exists
    const taskParam = params.get("task");
    
    // Clear all filter-related params
    params.delete("search");
    params.delete("status");
    params.delete("priority");
    params.delete("days");
    params.delete("showCancelled");
    params.delete("ownership");
    
    if (searchQuery)
      params.set("search", searchQuery);
    
    // Only set status if it's not the default (all except cancelled)
    const defaultStatus = ["todo", "inProgress", "done"];
    const isDefaultStatus = statusFilter.length === defaultStatus.length && 
      statusFilter.every(s => defaultStatus.includes(s));
    if (!isDefaultStatus && statusFilter.length > 0)
      params.set("status", statusFilter.join(","));
    
    if (priorityFilter.length > 0)
      params.set("priority", priorityFilter.join(","));
    
    if (maxDaysAgo !== null && maxDaysAgo !== 60)
      params.set("days", maxDaysAgo.toString());
    
    if (showCancelled)
      params.set("showCancelled", "true");
    
    if (ownershipFilter !== 'all')
      params.set("ownership", ownershipFilter);
    
    // Restore the task parameter if it existed
    if (taskParam)
      params.set("task", taskParam);
    
    setSearchParams(params, { replace: true });
  }, [searchQuery, statusFilter, priorityFilter, maxDaysAgo, showCancelled, ownershipFilter, searchParams]);
  
  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
  }, []);
  
  const setStatusFilter = useCallback((status: string[]) => {
    setStatusFilterState(status);
  }, []);
  
  const setPriorityFilter = useCallback((priority: string[]) => {
    setPriorityFilterState(priority);
  }, []);
  
  const setMaxDaysAgo = useCallback((days: number | null) => {
    setMaxDaysAgoState(days);
    setDaysFilterForEntity('tasks', days);
  }, [setDaysFilterForEntity]);
  
  const setShowCancelled = useCallback((show: boolean) => {
    setShowCancelledState(show);
  }, []);
  
  const setOwnershipFilter = useCallback((filter: 'my-tasks' | 'assigned-to-me' | 'all') => {
    setOwnershipFilterState(filter);
  }, []);

  const setSortField = useCallback((field: TasksSortField) => {
    setSortFieldState(field);
  }, []);

  const setSortOrder = useCallback((order: TasksSortOrder) => {
    setSortOrderState(order);
  }, []);

  const value = useMemo(() => ({
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    maxDaysAgo,
    setMaxDaysAgo,
    showCancelled,
    setShowCancelled,
    ownershipFilter,
    setOwnershipFilter,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    viewMode,
    setViewMode,
  }), [searchQuery, setSearchQuery, statusFilter, setStatusFilter, priorityFilter, setPriorityFilter, maxDaysAgo, setMaxDaysAgo, showCancelled, setShowCancelled, ownershipFilter, setOwnershipFilter, sortField, setSortField, sortOrder, setSortOrder, viewMode, setViewMode]);

  return (
    <TasksFilterContext.Provider value={value}>
      {children}
    </TasksFilterContext.Provider>
  );
};

export const useTasksFilter = () => {
  const context = useContext(TasksFilterContext);
  if (context === undefined) {
    throw new Error("useTasksFilter must be used within a TasksFilterProvider");
  }
  return context;
};
